/**
 * Cell components for UserTable rows. Extracted from UserTable to keep that
 * file under 500 lines.
 */

import { UserProfile } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Mail, Star } from 'lucide-react';

/**
 * Which companies this user is the designated primary contact for.
 *
 * A user can front several, so beyond the first the rest collapse into a
 * "+N more" chip — the full list stays available on hover rather than
 * stretching the row.
 */
export function PrimaryContactCell({ userProfile }: { userProfile: UserProfile }) {
  const assignments = userProfile.primary_contact_for ?? [];
  if (assignments.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  const names = assignments.map((a) => a.company_name);
  return (
    <div className="flex items-center gap-1.5" title={names.join(', ')}>
      <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 shrink-0" />
      <span className="text-sm">{names[0]}</span>
      {names.length > 1 && (
        <Badge variant="outline" className="text-xs">+{names.length - 1}</Badge>
      )}
    </div>
  );
}

/** Joined / Sent / Not Invited badge for the Invitation column. */
export function InvitationBadge({ userProfile }: { userProfile: UserProfile }) {
  if (userProfile.user.has_joined) {
    return (
      <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-xs">
        Joined
      </Badge>
    );
  }
  if (userProfile.user.invitation_sent) {
    return (
      <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 text-xs">
        <Mail className="h-3 w-3 mr-1" />Sent
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-gray-500 border-gray-300 text-xs">
      Not Invited
    </Badge>
  );
}
